import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  BankOutlined,
  ClusterOutlined,
  DownOutlined,
  DownloadOutlined,
  EditOutlined,
  FileExcelOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons'
import {
  Alert,
  Avatar,
  Button,
  Checkbox,
  Col,
  Drawer,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Tree,
  TreeSelect,
  Typography,
  Upload,
  message,
} from 'antd'
import type { DataNode } from 'antd/es/tree'
import type { FormInstance } from 'antd/es/form'
import dayjs from 'dayjs'
import { adminApi, type PageResult } from '../../api'
import {
  AppDatePicker,
  PageHeader,
  formatDateOnly,
  formatDateOrDash,
  tablePagination,
  useServerPagination,
} from '../../components/ui'
import './OrgStructure.css'

type OrgTreeNode = {
  id: number
  external_id: string
  source: string
  name: string
  code: string
  parent_id?: number | null
  unit_type: string
  is_active: boolean
  leader_name: string
  full_name?: string
  remark?: string
  branch_id?: number | null
  member_count: number
  sort_order?: number
  synced_at?: string | null
  children?: OrgTreeNode[]
}

const UNIT_TYPE_LABEL: Record<string, string> = {
  branch: '分部',
  department: '部门',
}

const ORG_SOURCE_LABEL: Record<string, string> = {
  manual: '手工录入',
  external: '外部同步',
  import: 'Excel 导入',
}

type PersonAccount = {
  status: 'none' | 'pending' | 'active'
  user_id?: number
  username?: string
  display_name?: string
  is_active?: boolean
  last_login_at?: string | null
}

type OrgPerson = {
  id: number
  name: string
  alias: string
  gender: string
  employee_no: string
  hire_date: string | null
  organization_id: number | null
  org_unit_id: number | null
  branch_id: number | null
  position: string
  job_level: string
  job_grade: string
  status: string
  office_location: string
  professional_title: string
  job_description: string
  email: string
  mobile: string
  phone: string
  other_phone?: string
  fax?: string
  office_room?: string
  user_id?: number | null
  activation_status?: string
  account_status?: string
  is_active: boolean
}

type OrgPersonDetail = OrgPerson & {
  has_avatar?: boolean
  updated_at?: string | null
  supervisor_id?: number | null
  other_supervisor_ids?: number[]
  assistant_ids?: number[]
  organization_name?: string
  org_unit_name?: string
  branch_name?: string
  supervisor_name?: string
  other_supervisor_names?: string[]
  assistant_names?: string[]
  account?: PersonAccount
}

type PersonOption = { id: number; name: string; label: string; employee_no: string }

const PERSON_STATUS_LABEL: Record<string, string> = {
  active: '在职',
  probation: '试用',
  left: '离职',
  suspended: '停职',
}

const PERSON_STATUS_OPTIONS = Object.entries(PERSON_STATUS_LABEL).map(([value, label]) => ({
  value,
  label,
}))

const GENDER_LABEL: Record<string, string> = {
  male: '男',
  female: '女',
}

const ACCOUNT_STATUS_LABEL: Record<string, string> = {
  pending: '未激活',
  active: '正常',
}

function PersonNameWithStatus({
  name,
  activationStatus,
  onClick,
}: {
  name: string
  activationStatus?: string
  onClick?: () => void
}) {
  const nameNode = onClick ? (
    <Button type="link" size="small" onClick={onClick}>
      <span className="org-structure-person-name-text">{name}</span>
    </Button>
  ) : (
    <span className="org-structure-person-name-text">{name}</span>
  )
  return (
    <span className="org-structure-person-name-inline">
      {nameNode}
      {activationStatus !== 'active' ? (
        <Typography.Text type="danger" style={{ marginLeft: 4, flexShrink: 0 }}>
          （未激活）
        </Typography.Text>
      ) : null}
    </span>
  )
}

function flattenOrgTree(nodes: OrgTreeNode[], map = new Map<number, OrgTreeNode>()): Map<number, OrgTreeNode> {
  for (const node of nodes) {
    map.set(node.id, node)
    if (node.children?.length) flattenOrgTree(node.children, map)
  }
  return map
}

function collectTreeKeys(nodes: OrgTreeNode[]): string[] {
  const keys: string[] = []
  const walk = (list: OrgTreeNode[]) => {
    for (const n of list) {
      keys.push(String(n.id))
      if (n.children?.length) walk(n.children)
    }
  }
  walk(nodes)
  return keys
}

function filterOrgTree(nodes: OrgTreeNode[], keyword: string): OrgTreeNode[] {
  const kw = keyword.trim().toLowerCase()
  if (!kw) return nodes
  const walk = (list: OrgTreeNode[]): OrgTreeNode[] => {
    const out: OrgTreeNode[] = []
    for (const node of list) {
      const children = node.children?.length ? walk(node.children) : []
      const selfMatch =
        node.name.toLowerCase().includes(kw) ||
        node.code.toLowerCase().includes(kw) ||
        node.external_id.toLowerCase().includes(kw)
      if (selfMatch || children.length) {
        out.push({ ...node, children: children.length ? children : undefined })
      }
    }
    return out
  }
  return walk(nodes)
}

function resolveBranchId(nodeId: number | null, nodeMap: Map<number, OrgTreeNode>): number | null {
  if (nodeId == null) return null
  let cur: OrgTreeNode | undefined = nodeMap.get(nodeId)
  while (cur) {
    if (cur.unit_type === 'branch') return cur.id
    if (cur.parent_id == null) break
    cur = nodeMap.get(cur.parent_id)
  }
  return null
}

function toParentTreeSelect(nodes: OrgTreeNode[]): { value: number; title: string; children?: ReturnType<typeof toParentTreeSelect> }[] {
  return nodes.map((n) => ({
    value: n.id,
    title: n.name,
    children: n.children?.length ? toParentTreeSelect(n.children) : undefined,
  }))
}

function collectDepartmentOptions(nodes: OrgTreeNode[], prefix = ''): { value: number; label: string }[] {
  const out: { value: number; label: string }[] = []
  for (const n of nodes) {
    const path = prefix ? `${prefix} / ${n.name}` : n.name
    if (n.unit_type === 'department') {
      out.push({ value: n.id, label: path })
    }
    if (n.children?.length) {
      out.push(...collectDepartmentOptions(n.children, n.unit_type === 'branch' ? n.name : path))
    }
  }
  return out
}

function toAntTreeNodes(nodes: OrgTreeNode[]): DataNode[] {
  return nodes.map((n) => ({
    key: String(n.id),
    title: (
      <div className="org-structure-tree-node">
        <ClusterOutlined className="org-structure-tree-node-icon" />
        <span className={`org-structure-tree-node-label${n.is_active ? '' : ' is-inactive'}`}>{n.name}</span>
        <span className="org-structure-tree-node-count">{n.member_count ?? 0}</span>
      </div>
    ),
    children: n.children?.length ? toAntTreeNodes(n.children) : undefined,
  }))
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="org-structure-info-row">
      <div className="org-structure-info-label">{label}</div>
      <div className="org-structure-info-value">{value ?? '—'}</div>
    </div>
  )
}

function personAvatarSrc(id: number, hasAvatar?: boolean, ts?: string | null) {
  if (!hasAvatar) return undefined
  return `/api/admin/org-units/persons/${id}/avatar${ts ? `?t=${encodeURIComponent(ts)}` : ''}`
}

function PersonDetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="org-structure-person-detail-section">
      <div className="org-structure-person-detail-section-title">{title}</div>
      <div className="org-structure-person-detail-section-body">{children}</div>
    </div>
  )
}

function PersonDetailField({
  label,
  value,
  fullWidth,
}: {
  label: string
  value: ReactNode
  fullWidth?: boolean
}) {
  return (
    <div
      className={`org-structure-person-detail-field${fullWidth ? ' org-structure-person-detail-field--full' : ''}`}
    >
      <span className="org-structure-person-detail-field-label">{label}</span>
      <span className="org-structure-person-detail-field-value">{value ?? '—'}</span>
    </div>
  )
}

type PersonFormBodyProps = {
  form: FormInstance
  departmentOptions: { value: number; label: string }[]
  branchOptions: { value: number; label: string }[]
  parentTreeSelectData: ReturnType<typeof toParentTreeSelect>
  personSelectOptions: { value: number; label: string }[]
  loadPersonOptions: (keyword?: string) => void
  avatarSlot?: ReactNode
  hideMobile?: boolean
}

function PersonFormBody({
  form,
  departmentOptions,
  branchOptions,
  parentTreeSelectData,
  personSelectOptions,
  loadPersonOptions,
  avatarSlot,
  hideMobile = false,
}: PersonFormBodyProps) {
  return (
    <Form form={form} layout="vertical">
      {avatarSlot ? <div style={{ marginBottom: 16 }}>{avatarSlot}</div> : null}
      <div className="org-structure-person-section">
        <Typography.Title level={5} className="org-structure-person-section-title">
          基本资料
        </Typography.Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="alias" label="别名">
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="gender" label="性别">
              <Radio.Group>
                <Radio value="male">男</Radio>
                <Radio value="female">女</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="employee_no" label="工号">
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="hire_date" label="入职时间">
              <AppDatePicker placeholder="选择日期" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="organization_id" label="组织">
              <TreeSelect
                allowClear
                showSearch
                treeDefaultExpandAll
                placeholder="请选择组织"
                treeData={parentTreeSelectData}
                treeNodeFilterProp="title"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="org_unit_id" label="部门">
              <Select
                allowClear
                showSearch
                placeholder="请选择部门"
                options={departmentOptions}
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="branch_id" label="分部">
              <Select allowClear placeholder="请选择分部" options={branchOptions} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="position" label="岗位">
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="job_level" label="职级">
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="job_grade" label="职等">
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="status" label="状态">
              <Select options={PERSON_STATUS_OPTIONS} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="office_location" label="办公地点">
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="professional_title" label="职称">
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="job_description" label="职责描述">
              <Input.TextArea rows={3} placeholder="请输入" />
            </Form.Item>
          </Col>
        </Row>
      </div>
      <div className="org-structure-person-section">
        <Typography.Title level={5} className="org-structure-person-section-title">
          通讯信息
        </Typography.Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="email" label="邮箱">
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
          {hideMobile ? null : (
            <Col span={12}>
              <Form.Item name="mobile" label="手机">
                <Input placeholder="请输入" />
              </Form.Item>
            </Col>
          )}
          <Col span={12}>
            <Form.Item name="phone" label="电话">
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="other_phone" label="其他电话">
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="fax" label="传真">
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="office_room" label="办公室">
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
        </Row>
      </div>
      <div className="org-structure-person-section">
        <Typography.Title level={5} className="org-structure-person-section-title">
          上下级关系
        </Typography.Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="supervisor_id" label="上级">
              <Select
                allowClear
                showSearch
                placeholder="请选择上级"
                options={personSelectOptions}
                optionFilterProp="label"
                onSearch={loadPersonOptions}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="other_supervisor_ids" label="其他上级">
              <Select
                allowClear
                mode="multiple"
                showSearch
                placeholder="请选择"
                options={personSelectOptions}
                optionFilterProp="label"
                onSearch={loadPersonOptions}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="assistant_ids" label="助理">
              <Select
                allowClear
                mode="multiple"
                showSearch
                placeholder="请选择"
                options={personSelectOptions}
                optionFilterProp="label"
                onSearch={loadPersonOptions}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>
    </Form>
  )
}

export default function OrgStructure() {
  const [tree, setTree] = useState<OrgTreeNode[]>([])
  const [loadingTree, setLoadingTree] = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])
  const [detailTab, setDetailTab] = useState('info')

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()
  const [syncing, setSyncing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deptDrawerOpen, setDeptDrawerOpen] = useState(false)
  const [deptForm] = Form.useForm()
  const [memberDrawerOpen, setMemberDrawerOpen] = useState(false)
  const [memberForm] = Form.useForm()
  const [personDetailOpen, setPersonDetailOpen] = useState(false)
  const [personDetailTab, setPersonDetailTab] = useState('basic')
  const [personDetail, setPersonDetail] = useState<OrgPersonDetail | null>(null)
  const [loadingPersonDetail, setLoadingPersonDetail] = useState(false)
  const [personEditing, setPersonEditing] = useState(false)
  const [personEditForm] = Form.useForm()
  const [assignAccountOpen, setAssignAccountOpen] = useState(false)
  const [assignAccountForm] = Form.useForm()
  const [assignedCredentials, setAssignedCredentials] = useState<{
    personId: number
    username: string
    password: string
  } | null>(null)
  const [avatarUploading, setAvatarUploading] = useState(false)

  const [persons, setPersons] = useState<OrgPerson[]>([])
  const [personTotal, setPersonTotal] = useState(0)
  const [loadingPersons, setLoadingPersons] = useState(false)
  const [personOptions, setPersonOptions] = useState<PersonOption[]>([])
  const {
    page: memberPage,
    pageSize: memberPageSize,
    handlePaginationChange: onMemberPageChange,
    paginationOptions: memberPaginationOptions,
  } = useServerPagination()

  const loadTree = useCallback(() => {
    setLoadingTree(true)
    adminApi
      .get<{ items: OrgTreeNode[]; total: number }>('/admin/org-units/tree', {
        params: { active_only: !showInactive },
      })
      .then((r) => {
        const items = r.data.items || []
        setTree(items)
        const keys = collectTreeKeys(items)
        setExpandedKeys(keys)
        setSelectedId((prev) => {
          if (prev != null && keys.includes(String(prev))) return prev
          return items[0]?.id ?? null
        })
      })
      .catch((e) => message.error(e.message))
      .finally(() => setLoadingTree(false))
  }, [showInactive])

  const loadPersons = useCallback((unitId: number, p: number, ps: number) => {
    setLoadingPersons(true)
    adminApi
      .get<PageResult<OrgPerson>>(`/admin/org-units/${unitId}/persons`, {
        params: { page: p, page_size: ps },
      })
      .then((r) => {
        setPersons(r.data.items)
        setPersonTotal(r.data.total)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setLoadingPersons(false))
  }, [])

  const loadPersonOptions = useCallback((keyword?: string) => {
    adminApi
      .get<{ items: PersonOption[] }>('/admin/org-units/persons/options', {
        params: { keyword: keyword || undefined, limit: 100 },
      })
      .then((r) => setPersonOptions(r.data.items || []))
      .catch((e) => message.error(e.message))
  }, [])

  useEffect(() => {
    loadTree()
  }, [loadTree])

  useEffect(() => {
    if (detailTab === 'hr' && selectedId != null) {
      loadPersons(selectedId, memberPage, memberPageSize)
    }
  }, [detailTab, selectedId, memberPage, memberPageSize, loadPersons])

  const nodeMap = useMemo(() => flattenOrgTree(tree), [tree])
  const filteredTree = useMemo(() => filterOrgTree(tree, keyword), [tree, keyword])
  const treeData = useMemo(() => toAntTreeNodes(filteredTree), [filteredTree])
  const selected = selectedId != null ? nodeMap.get(selectedId) ?? null : null
  const directChildren = selected?.children ?? []
  const childDepartments = useMemo(
    () => directChildren.filter((c) => c.unit_type === 'department'),
    [directChildren],
  )
  const childBranches = useMemo(
    () => directChildren.filter((c) => c.unit_type === 'branch'),
    [directChildren],
  )
  const branchOptions = useMemo(() => {
    const list: { value: number; label: string }[] = []
    for (const [, node] of nodeMap) {
      if (node.unit_type === 'branch') {
        list.push({ value: node.id, label: node.name })
      }
    }
    return list.sort((a, b) => a.label.localeCompare(b.label, 'zh-CN'))
  }, [nodeMap])
  const parentTreeSelectData = useMemo(() => toParentTreeSelect(tree), [tree])
  const departmentOptions = useMemo(() => collectDepartmentOptions(tree), [tree])
  const personSelectOptions = useMemo(
    () => personOptions.map((p) => ({ value: p.id, label: p.label || p.name })),
    [personOptions],
  )
  const selectedBranchName =
    selected?.branch_id != null ? nodeMap.get(selected.branch_id)?.name ?? '—' : '—'

  useEffect(() => {
    if (keyword.trim()) {
      setExpandedKeys(collectTreeKeys(filteredTree))
    }
  }, [keyword, filteredTree])

  const openCreateBranch = () => {
    createForm.resetFields()
    createForm.setFieldsValue({
      unit_type: 'branch',
      sort_order: 0,
      parent_id: selectedId,
    })
    setCreateOpen(true)
  }

  const openDepartmentDrawer = (preset?: { parent_id?: number | null; branch_id?: number | null }) => {
    deptForm.resetFields()
    const parentId = preset?.parent_id !== undefined ? preset.parent_id : selectedId
    const branchId =
      preset?.branch_id !== undefined
        ? preset.branch_id
        : selected
          ? selected.unit_type === 'branch'
            ? selected.id
            : resolveBranchId(selected.id, nodeMap)
          : null
    deptForm.setFieldsValue({
      parent_id: parentId,
      branch_id: branchId,
      sort_order: 0,
      remark: '',
      full_name: '',
    })
    setDeptDrawerOpen(true)
  }

  const submitDepartment = () => {
    void deptForm.validateFields().then((v) => {
      setSaving(true)
      adminApi
        .post('/admin/org-units', {
          ...v,
          unit_type: 'department',
        })
        .then((r) => {
          message.success('部门已保存')
          setDeptDrawerOpen(false)
          loadTree()
          if (r.data.id) setSelectedId(r.data.id)
        })
        .catch((e) => message.error(e.message))
        .finally(() => setSaving(false))
    })
  }

  const openMemberDrawer = () => {
    if (selectedId == null || !selected) return
    loadPersonOptions()
    memberForm.resetFields()
    const branchId =
      selected.unit_type === 'branch' ? selected.id : resolveBranchId(selected.id, nodeMap)
    const orgUnitId = selected.unit_type === 'department' ? selected.id : null
    memberForm.setFieldsValue({
      organization_id: branchId ?? selectedId,
      org_unit_id: orgUnitId,
      branch_id: branchId,
      gender: 'male',
      status: 'probation',
      is_active: true,
      other_supervisor_ids: [],
      assistant_ids: [],
    })
    setMemberDrawerOpen(true)
  }

  const loadPersonDetail = useCallback((personId: number) => {
    setLoadingPersonDetail(true)
    adminApi
      .get<OrgPersonDetail>(`/admin/org-units/persons/${personId}`)
      .then((r) => setPersonDetail(r.data))
      .catch((e) => message.error(e.message))
      .finally(() => setLoadingPersonDetail(false))
  }, [])

  const openPersonDetail = (personId: number, tab = 'basic') => {
    setAssignedCredentials((prev) => (prev?.personId === personId ? prev : null))
    setPersonDetailTab(tab)
    setPersonEditing(false)
    setPersonDetailOpen(true)
    loadPersonDetail(personId)
  }

  const startPersonEdit = () => {
    if (!personDetail) return
    loadPersonOptions()
    personEditForm.resetFields()
    personEditForm.setFieldsValue({
      name: personDetail.name,
      alias: personDetail.alias,
      gender: personDetail.gender || 'male',
      employee_no: personDetail.employee_no,
      hire_date: personDetail.hire_date ? dayjs(personDetail.hire_date) : null,
      organization_id: personDetail.organization_id,
      org_unit_id: personDetail.org_unit_id,
      branch_id: personDetail.branch_id,
      position: personDetail.position,
      job_level: personDetail.job_level,
      job_grade: personDetail.job_grade,
      status: personDetail.status,
      office_location: personDetail.office_location,
      professional_title: personDetail.professional_title,
      job_description: personDetail.job_description,
      email: personDetail.email,
      mobile: personDetail.mobile,
      phone: personDetail.phone,
      other_phone: personDetail.other_phone,
      fax: personDetail.fax,
      office_room: personDetail.office_room,
      supervisor_id: personDetail.supervisor_id,
      other_supervisor_ids: personDetail.other_supervisor_ids || [],
      assistant_ids: personDetail.assistant_ids || [],
    })
    setPersonEditing(true)
  }

  const cancelPersonEdit = () => {
    setPersonEditing(false)
    if (personDetail) loadPersonDetail(personDetail.id)
  }

  const submitPersonEdit = () => {
    if (!personDetail) return
    void personEditForm.validateFields().then((v) => {
      setSaving(true)
      adminApi
        .put<OrgPersonDetail>(`/admin/org-units/persons/${personDetail.id}`, {
          ...v,
          hire_date: v.hire_date ? dayjs(v.hire_date).format('YYYY-MM-DD') : null,
          other_supervisor_ids: v.other_supervisor_ids || [],
          assistant_ids: v.assistant_ids || [],
        })
        .then((r) => {
          message.success('已保存')
          setPersonDetail(r.data)
          setPersonEditing(false)
          if (selectedId != null) {
            loadPersons(selectedId, memberPage, memberPageSize)
          }
        })
        .catch((e) => message.error(e.message))
        .finally(() => setSaving(false))
    })
  }

  const uploadPersonAvatar = (file: File) => {
    if (!personDetail) return Upload.LIST_IGNORE
    const fd = new FormData()
    fd.append('file', file)
    setAvatarUploading(true)
    adminApi
      .post<OrgPersonDetail>(`/admin/org-units/persons/${personDetail.id}/avatar`, fd)
      .then((r) => {
        setPersonDetail(r.data)
        message.success('头像已更新')
      })
      .catch((e) => message.error(e.message))
      .finally(() => setAvatarUploading(false))
    return false
  }

  const handleAssignAccount = () => {
    if (!personDetail) return
    if (personDetail.user_id) {
      message.info('该人员已分配账号')
      return
    }
    const profileMobile = (personDetail.mobile || '').trim()
    assignAccountForm.resetFields()
    assignAccountForm.setFieldsValue({
      useProfileMobile: Boolean(profileMobile),
      mobile: profileMobile,
    })
    setAssignAccountOpen(true)
  }

  const submitAssignAccount = () => {
    if (!personDetail) return
    void assignAccountForm.validateFields().then((v) => {
      const profileMobile = (personDetail.mobile || '').trim()
      const mobile =
        v.useProfileMobile && profileMobile ? profileMobile : (v.mobile || '').trim()
      if (!/^1\d{10}$/.test(mobile)) {
        message.error('请输入 11 位手机号')
        return
      }
      setSaving(true)
      adminApi
        .post<OrgPersonDetail & { initial_password?: string }>(
          `/admin/org-units/persons/${personDetail.id}/assign-account`,
          { mobile },
        )
        .then((r) => {
          setAssignAccountOpen(false)
          setPersonDetail(r.data)
          setPersonDetailTab('account')
          if (r.data.initial_password) {
            const username = r.data.account?.username || ''
            const password = r.data.initial_password
            setAssignedCredentials({ personId: r.data.id, username, password })
            const copyText = `用户名：${username}\n初始密码：${password}`
            Modal.info({
              title: '账号已创建 — 请保存初始密码',
              okText: '确定',
              content: (
                <div>
                  <p>
                    <strong>用户名：</strong>
                    {username}
                  </p>
                  <p>
                    <strong>初始密码：</strong>
                    <Typography.Text copyable={{ text: password }}>{password}</Typography.Text>
                  </p>
                  <Typography.Paragraph type="warning" style={{ marginBottom: 0 }}>
                    初始密码仅展示一次，请立即复制或通知用户。用户首次登录后，姓名旁的「未激活」才会消失。
                  </Typography.Paragraph>
                </div>
              ),
              footer: (_, { OkBtn }) => (
                <Space>
                  <Button
                    onClick={() => {
                      void navigator.clipboard
                        .writeText(copyText)
                        .then(() => message.success('已复制账号信息'))
                        .catch(() => message.error('复制失败，请手动复制'))
                    }}
                  >
                    复制账号信息
                  </Button>
                  <OkBtn />
                </Space>
              ),
            })
          } else {
            setAssignedCredentials(null)
            message.success(
              '已绑定用户管理中已有账号；初始密码以用户管理中的设置为准。用户首次登录后姓名旁的「未激活」才会消失。',
            )
          }
          if (selectedId != null) {
            loadPersons(selectedId, memberPage, memberPageSize)
          }
        })
        .catch((e) => message.error(e.message))
        .finally(() => setSaving(false))
    })
  }

  const submitMember = () => {
    void memberForm.validateFields().then((v) => {
      setSaving(true)
      adminApi
        .post('/admin/org-units/persons', {
          ...v,
          hire_date: v.hire_date ? dayjs(v.hire_date).format('YYYY-MM-DD') : null,
          other_supervisor_ids: v.other_supervisor_ids || [],
          assistant_ids: v.assistant_ids || [],
        })
        .then(() => {
          message.success('人员已保存')
          setMemberDrawerOpen(false)
          if (selectedId != null) {
            loadPersons(selectedId, memberPage, memberPageSize)
            loadPersonOptions()
          }
        })
        .catch((e) => message.error(e.message))
        .finally(() => setSaving(false))
    })
  }

  const submitCreate = () => {
    void createForm.validateFields().then((v) => {
      setSaving(true)
      adminApi
        .post('/admin/org-units', v)
        .then((r) => {
          message.success('已创建')
          setCreateOpen(false)
          loadTree()
          if (r.data.id) setSelectedId(r.data.id)
        })
        .catch((e) => message.error(e.message))
        .finally(() => setSaving(false))
    })
  }

  const openEdit = () => {
    if (!selected) return
    editForm.setFieldsValue({
      name: selected.name,
      code: selected.code,
      leader_name: selected.leader_name,
      sort_order: selected.sort_order ?? 0,
      is_active: selected.is_active,
    })
    setEditOpen(true)
  }

  const submitEdit = () => {
    if (!selected) return
    void editForm.validateFields().then((v) => {
      setSaving(true)
      adminApi
        .put(`/admin/org-units/${selected.id}`, v)
        .then(() => {
          message.success('已保存')
          setEditOpen(false)
          loadTree()
        })
        .catch((e) => message.error(e.message))
        .finally(() => setSaving(false))
    })
  }

  const downloadImportTemplate = () => {
    adminApi
      .get('/admin/org-units/import/template', { responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(r.data as Blob)
        const a = document.createElement('a')
        a.href = url
        a.download = '组织架构导入模板.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      })
      .catch((e) => message.error(e.message))
  }

  const runImportUpload = (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    setSyncing(true)
    adminApi
      .post<{
        stats: {
          deleted?: number
          created?: number
          total?: number
          persons_created?: number
          persons_deleted?: number
        }
      }>(
        '/admin/org-units/import',
        fd,
      )
      .then((r) => {
        const s = r.data.stats
        message.success(
          `导入完成：组织 ${s?.created ?? 0} 条，人员 ${s?.persons_created ?? 0} 条（已覆盖原组织 ${s?.deleted ?? 0} 条、原人员 ${s?.persons_deleted ?? 0} 条）`,
        )
        loadTree()
        setSelectedId(null)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setSyncing(false))
    return false
  }

  const confirmImport = (file: File) => {
    Modal.confirm({
      title: '确认导入组织架构？',
      icon: <FileExcelOutlined />,
      content:
        '导入将删除当前全部组织与人员档案并用 Excel 重建；系统账号保留，但组织/人员关联将被清空。此操作不可撤销，请确认已备份。',
      okText: '确认导入',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => runImportUpload(file),
    })
  }

  const syncMenuItems = [
    {
      key: 'import-template',
      icon: <DownloadOutlined />,
      label: '下载导入模板',
      onClick: downloadImportTemplate,
    },
    {
      key: 'import',
      icon: <FileExcelOutlined />,
      label: 'Excel 导入组织',
      onClick: () => {
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        input.onchange = () => {
          const file = input.files?.[0]
          if (file) confirmImport(file)
        }
        input.click()
      },
    },
    { key: 'refresh', icon: <ReloadOutlined />, label: '刷新组织树', onClick: loadTree },
  ]

  const createDropdownItems = [
    {
      key: 'department',
      label: '新建下级部门',
      onClick: () => openDepartmentDrawer({ parent_id: selectedId }),
    },
    { key: 'branch', label: '新建下级分部', onClick: openCreateBranch },
  ]

  const childUnitColumns = [
    { title: '名称', dataIndex: 'name' as const },
    { title: '编码', dataIndex: 'code' as const, width: 100 },
    { title: '人数', dataIndex: 'member_count' as const, width: 72 },
    {
      title: '状态',
      width: 72,
      render: (_: unknown, r: OrgTreeNode) =>
        r.is_active ? <Tag color="green">正常</Tag> : <Tag>停用</Tag>,
    },
    {
      title: '操作',
      width: 72,
      render: (_: unknown, r: OrgTreeNode) => (
        <Button type="link" size="small" onClick={() => setSelectedId(r.id)}>
          查看
        </Button>
      ),
    },
  ]

  const renderToolbarActions = () => {
    if (detailTab === 'info') {
      return (
        <Space wrap>
          <Button icon={<EditOutlined />} onClick={openEdit}>
            编辑
          </Button>
          <Dropdown.Button
            type="primary"
            icon={<DownOutlined />}
            menu={{ items: createDropdownItems }}
            onClick={() => openDepartmentDrawer()}
          >
            新建部门
          </Dropdown.Button>
        </Space>
      )
    }
    if (detailTab === 'departments') {
      return (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openDepartmentDrawer({ parent_id: selectedId })}>
          新建部门
        </Button>
      )
    }
    if (detailTab === 'branches') {
      return (
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateBranch}>
          新建分部
        </Button>
      )
    }
    if (detailTab === 'hr') {
      return (
        <Button type="primary" icon={<PlusOutlined />} onClick={openMemberDrawer}>
          新建人员
        </Button>
      )
    }
    return null
  }

  return (
    <div className="org-structure-page">
      <PageHeader
        title="组织架构"
        description="左侧为行政组织树；人数统计以「用户管理」中归属本组织的账号为准。外部拉取/推送同步请至「组织同步」。"
      />

      <div className="org-structure-shell">
        <aside className="org-structure-sider">
          <div className="org-structure-sider-tabs">
            <Tabs items={[{ key: 'org', label: '组织' }]} activeKey="org" size="small" />
          </div>
          <div className="org-structure-sider-search">
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
              placeholder="请输入关键字"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>
          <div className="org-structure-sider-head">
            <span className="org-structure-sider-head-title">行政组织</span>
            <Space size={8}>
              <Tooltip title="勾选后可看到已在 HR/OA 中停用、但保留历史的组织节点">
                <Checkbox checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}>
                  <span className="org-structure-sider-filter">显示已停用</span>
                </Checkbox>
              </Tooltip>
              <Dropdown menu={{ items: syncMenuItems }} trigger={['click']}>
                <Button type="text" size="small" icon={<MoreOutlined />} loading={syncing} />
              </Dropdown>
            </Space>
          </div>
          <div className="org-structure-tree-wrap">
            <Spin spinning={loadingTree}>
              {treeData.length ? (
                <Tree
                  blockNode
                  showLine={{ showLeafIcon: false }}
                  treeData={treeData}
                  selectedKeys={selectedId != null ? [String(selectedId)] : []}
                  expandedKeys={expandedKeys}
                  onExpand={(keys) => setExpandedKeys(keys as string[])}
                  onSelect={(keys) => {
                    if (keys[0]) setSelectedId(Number(keys[0]))
                  }}
                />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={loadingTree ? '加载中…' : '暂无组织，请同步或新建'}
                  style={{ marginTop: 48 }}
                />
              )}
            </Spin>
          </div>
        </aside>

        <main className="org-structure-main">
          {selected ? (
            <>
              <div className="org-structure-main-head">
                <div className="org-structure-main-title">
                  <div className="org-structure-main-title-icon">
                    <BankOutlined />
                  </div>
                  <Typography.Title level={5} className="org-structure-main-title-text" ellipsis>
                    {selected.name}
                  </Typography.Title>
                  <Tag>{UNIT_TYPE_LABEL[selected.unit_type] || '部门'}</Tag>
                  {!selected.is_active ? <Tag color="default">已停用</Tag> : null}
                </div>
              </div>

              {renderToolbarActions() ? (
                <div className="org-structure-main-toolbar">{renderToolbarActions()}</div>
              ) : null}

              <div className="org-structure-main-body">
                <Tabs
                  activeKey={detailTab}
                  onChange={setDetailTab}
                  items={[
                    {
                      key: 'info',
                      label: '组织信息',
                      children: (
                        <div className="org-structure-info-grid">
                          <InfoRow label="组织名称" value={selected.name} />
                          <InfoRow label="组织类型" value={UNIT_TYPE_LABEL[selected.unit_type] || '部门'} />
                          <InfoRow label="组织编码" value={selected.code || '—'} />
                          <InfoRow label="部门全称" value={selected.full_name || '—'} />
                          <InfoRow label="所属分部" value={selectedBranchName} />
                          <InfoRow label="在职人数" value={selected.member_count ?? 0} />
                          <InfoRow label="负责人" value={selected.leader_name || '—'} />
                          <InfoRow label="显示顺序" value={selected.sort_order ?? 0} />
                          <InfoRow label="备注" value={selected.remark || '—'} />
                          {selected.source && selected.source !== 'manual' ? (
                            <InfoRow
                              label="数据来源"
                              value={ORG_SOURCE_LABEL[selected.source] || selected.source}
                            />
                          ) : null}
                          <InfoRow
                            label="状态"
                            value={selected.is_active ? <Tag color="green">正常</Tag> : <Tag>已停用</Tag>}
                          />
                          <InfoRow label="最近同步" value={formatDateOrDash(selected.synced_at ?? null)} />
                        </div>
                      ),
                    },
                    {
                      key: 'departments',
                      label: '下级部门',
                      children: (
                        <Table<OrgTreeNode>
                          rowKey="id"
                          size="small"
                          pagination={false}
                          dataSource={childDepartments}
                          locale={{ emptyText: '暂无下级部门' }}
                          columns={childUnitColumns}
                        />
                      ),
                    },
                    {
                      key: 'branches',
                      label: '下级分部',
                      children: (
                        <Table<OrgTreeNode>
                          rowKey="id"
                          size="small"
                          pagination={false}
                          dataSource={childBranches}
                          locale={{ emptyText: '暂无下级分部' }}
                          columns={childUnitColumns}
                        />
                      ),
                    },
                    {
                      key: 'hr',
                      label: '人力资源',
                      children: (
                        <Table<OrgPerson>
                          rowKey="id"
                          size="small"
                          loading={loadingPersons}
                          dataSource={persons}
                          locale={{ emptyText: '暂无人员，点击右上角新建人员' }}
                          pagination={tablePagination(
                            memberPage,
                            personTotal,
                            onMemberPageChange,
                            memberPaginationOptions,
                          )}
                          columns={[
                            {
                              title: '姓名',
                              dataIndex: 'name',
                              width: 148,
                              ellipsis: true,
                              render: (name: string, r) => (
                                <PersonNameWithStatus
                                  name={name}
                                  activationStatus={r.activation_status ?? 'pending'}
                                  onClick={() => openPersonDetail(r.id)}
                                />
                              ),
                            },
                            { title: '工号', dataIndex: 'employee_no', width: 100 },
                            { title: '岗位', dataIndex: 'position', ellipsis: true },
                            { title: '手机', dataIndex: 'mobile', width: 120 },
                            {
                              title: '账号',
                              width: 72,
                              render: (_, r) => {
                                const st = r.account_status || 'pending'
                                return (
                                  <Tag color={st === 'active' ? 'green' : 'orange'}>
                                    {ACCOUNT_STATUS_LABEL[st] || st}
                                  </Tag>
                                )
                              },
                            },
                            {
                              title: '在职',
                              width: 72,
                              render: (_, r) => (
                                <Tag color={r.status === 'active' ? 'green' : 'default'}>
                                  {PERSON_STATUS_LABEL[r.status] || r.status || '—'}
                                </Tag>
                              ),
                            },
                          ]}
                        />
                      ),
                    },
                  ]}
                />
              </div>
            </>
          ) : (
            <div className="org-structure-empty-main">
              <Empty description="请在左侧选择组织节点" />
            </div>
          )}
        </main>
      </div>

      <Drawer
        title="新建部门"
        open={deptDrawerOpen}
        onClose={() => setDeptDrawerOpen(false)}
        width={480}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => setDeptDrawerOpen(false)}>取消</Button>
            <Button type="primary" loading={saving} onClick={submitDepartment}>
              保存
            </Button>
          </Space>
        }
      >
        <Form form={deptForm} layout="vertical">
          <Form.Item name="name" label="部门名称" rules={[{ required: true, message: '请输入部门名称' }]}>
            <Input placeholder="如 研发部" />
          </Form.Item>
          <Form.Item name="code" label="部门编号">
            <Input placeholder="可选" />
          </Form.Item>
          <Form.Item name="parent_id" label="上级部门">
            <TreeSelect
              allowClear
              showSearch
              treeDefaultExpandAll
              placeholder="请选择上级部门"
              treeData={parentTreeSelectData}
              treeNodeFilterProp="title"
            />
          </Form.Item>
          <Form.Item name="branch_id" label="所属分部">
            <Select allowClear placeholder="请选择所属分部" options={branchOptions} />
          </Form.Item>
          <Form.Item name="sort_order" label="显示顺序">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} placeholder="请输入" />
          </Form.Item>
          <Form.Item name="full_name" label="部门全称">
            <Input placeholder="可选，默认可与部门名称相同" />
          </Form.Item>
        </Form>
      </Drawer>

      <Drawer
        title={personDetail?.name ? `${personDetail.name} — 人员详情` : '人员详情'}
        open={personDetailOpen}
        onClose={() => {
          setPersonDetailOpen(false)
          setPersonDetail(null)
          setPersonEditing(false)
        }}
        width={720}
        destroyOnClose
        extra={
          personDetailTab === 'basic' ? (
            personEditing ? (
              <Space>
                <Button onClick={cancelPersonEdit}>取消</Button>
                <Button type="primary" loading={saving} onClick={submitPersonEdit}>
                  保存
                </Button>
              </Space>
            ) : (
              <Button icon={<EditOutlined />} onClick={startPersonEdit}>
                编辑
              </Button>
            )
          ) : null
        }
      >
        <Spin spinning={loadingPersonDetail}>
          {personDetail ? (
            <Tabs
              activeKey={personDetailTab}
              onChange={(key) => {
                if (personEditing && key !== 'basic') cancelPersonEdit()
                setPersonDetailTab(key)
              }}
              items={[
                {
                  key: 'basic',
                  label: '基本信息',
                  children: personEditing ? (
                    <PersonFormBody
                      form={personEditForm}
                      departmentOptions={departmentOptions}
                      branchOptions={branchOptions}
                      parentTreeSelectData={parentTreeSelectData}
                      personSelectOptions={personSelectOptions}
                      loadPersonOptions={loadPersonOptions}
                      hideMobile
                      avatarSlot={
                        <Upload
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          showUploadList={false}
                          beforeUpload={uploadPersonAvatar}
                        >
                          <div className="org-structure-person-detail-head" style={{ marginBottom: 0, border: 0 }}>
                            <Avatar
                              size={72}
                              src={personAvatarSrc(personDetail.id, personDetail.has_avatar, personDetail.updated_at)}
                              icon={<UserOutlined />}
                            />
                            <div className="org-structure-person-detail-head-meta">
                              <Typography.Text type="secondary">点击头像上传（JPG/PNG，最大 2MB）</Typography.Text>
                              {avatarUploading ? <Spin size="small" style={{ marginLeft: 8 }} /> : null}
                            </div>
                          </div>
                        </Upload>
                      }
                    />
                  ) : (
                    <>
                      <div className="org-structure-person-detail-head">
                        <Avatar
                          size={72}
                          src={personAvatarSrc(personDetail.id, personDetail.has_avatar, personDetail.updated_at)}
                          icon={<UserOutlined />}
                        />
                        <div className="org-structure-person-detail-head-meta">
                          <Typography.Title level={5} className="org-structure-person-detail-head-name">
                            <PersonNameWithStatus
                              name={personDetail.name}
                              activationStatus={personDetail.activation_status ?? 'pending'}
                            />
                          </Typography.Title>
                          <Typography.Text type="secondary">
                            {[personDetail.employee_no, personDetail.position].filter(Boolean).join(' · ') || '—'}
                          </Typography.Text>
                        </div>
                      </div>
                      <PersonDetailSection title="基本资料">
                        <PersonDetailField label="姓名" value={personDetail.name} />
                        <PersonDetailField label="别名" value={personDetail.alias || '—'} />
                        <PersonDetailField label="性别" value={GENDER_LABEL[personDetail.gender] || '—'} />
                        <PersonDetailField label="工号" value={personDetail.employee_no || '—'} />
                        <PersonDetailField
                          label="入职时间"
                          value={personDetail.hire_date ? formatDateOnly(personDetail.hire_date) : '—'}
                        />
                        <PersonDetailField label="组织" value={personDetail.organization_name || '—'} />
                        <PersonDetailField label="部门" value={personDetail.org_unit_name || '—'} />
                        <PersonDetailField label="分部" value={personDetail.branch_name || '—'} />
                        <PersonDetailField label="岗位" value={personDetail.position || '—'} />
                        <PersonDetailField label="职级" value={personDetail.job_level || '—'} />
                        <PersonDetailField label="职等" value={personDetail.job_grade || '—'} />
                        <PersonDetailField
                          label="状态"
                          value={PERSON_STATUS_LABEL[personDetail.status] || personDetail.status || '—'}
                        />
                        <PersonDetailField label="办公地点" value={personDetail.office_location || '—'} />
                        <PersonDetailField label="职称" value={personDetail.professional_title || '—'} />
                        <PersonDetailField
                          label="职责描述"
                          value={personDetail.job_description || '—'}
                          fullWidth
                        />
                      </PersonDetailSection>
                      <PersonDetailSection title="通讯信息">
                        <PersonDetailField label="邮箱" value={personDetail.email || '—'} />
                        <PersonDetailField
                          label="手机"
                          value={personDetail.mobile || personDetail.account?.username || '—'}
                        />
                        <PersonDetailField label="电话" value={personDetail.phone || '—'} />
                        <PersonDetailField label="其他电话" value={personDetail.other_phone || '—'} />
                        <PersonDetailField label="传真" value={personDetail.fax || '—'} />
                        <PersonDetailField label="办公室" value={personDetail.office_room || '—'} />
                      </PersonDetailSection>
                      <PersonDetailSection title="上下级关系">
                        <PersonDetailField label="上级" value={personDetail.supervisor_name || '—'} />
                        <PersonDetailField
                          label="其他上级"
                          value={
                            personDetail.other_supervisor_names?.length
                              ? personDetail.other_supervisor_names.join('、')
                              : '—'
                          }
                        />
                        <PersonDetailField
                          label="助理"
                          value={
                            personDetail.assistant_names?.length
                              ? personDetail.assistant_names.join('、')
                              : '—'
                          }
                        />
                      </PersonDetailSection>
                    </>
                  ),
                },
                {
                  key: 'account',
                  label: '账号信息',
                  children: (
                    <div className="org-structure-account-panel">
                      {!personDetail.user_id ? (
                        <Empty description="尚未分配系统登录账号">
                          <Button type="primary" onClick={handleAssignAccount}>
                            分配账号
                          </Button>
                        </Empty>
                      ) : (
                        <>
                        {assignedCredentials?.personId === personDetail.id ? (
                          <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="初始密码（仅本次可见，关闭详情后不再显示）"
                            description={
                              <div>
                                <p style={{ margin: '4px 0' }}>
                                  用户名：{assignedCredentials.username}
                                </p>
                                <p style={{ margin: '4px 0' }}>
                                  初始密码：
                                  <Typography.Text copyable={{ text: assignedCredentials.password }}>
                                    {assignedCredentials.password}
                                  </Typography.Text>
                                </p>
                              </div>
                            }
                          />
                        ) : null}
                        <div className="org-structure-info-grid">
                          <InfoRow label="登录用户名" value={personDetail.account?.username || '—'} />
                          <InfoRow label="显示名" value={personDetail.account?.display_name || '—'} />
                          <InfoRow
                            label="账号状态"
                            value={
                              <Tag
                                color={
                                  personDetail.account?.status === 'active'
                                    ? 'green'
                                    : personDetail.account?.status === 'pending'
                                      ? 'orange'
                                      : 'default'
                                }
                              >
                                {ACCOUNT_STATUS_LABEL[personDetail.account?.status || 'none']}
                              </Tag>
                            }
                          />
                          <InfoRow
                            label="最近登录"
                            value={formatDateOrDash(personDetail.account?.last_login_at ?? null)}
                          />
                          {personDetail.activation_status !== 'active' ? (
                            <InfoRow
                              label="说明"
                              value="账号已分配；用户首次登录后姓名旁的「未激活」标识才会消失"
                            />
                          ) : null}
                          {!assignedCredentials || assignedCredentials.personId !== personDetail.id ? (
                            <InfoRow
                              label="密码"
                              value={
                                personDetail.account?.last_login_at
                                  ? '用户已登录，如需修改请前往用户管理重置密码'
                                  : '新建账号的初始密码仅在分配成功时展示；若未保存，请在用户管理中重置密码'
                              }
                            />
                          ) : null}
                        </div>
                        </>
                      )}
                      {personDetail.user_id ? (
                        <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
                          账号与人员手机号绑定，用户名为手机号。
                        </Typography.Paragraph>
                      ) : null}
                    </div>
                  ),
                },
              ]}
            />
          ) : (
            <Empty description="加载中…" />
          )}
        </Spin>
      </Drawer>

      <Drawer
        title="新建人员"
        open={memberDrawerOpen}
        onClose={() => setMemberDrawerOpen(false)}
        width={720}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => setMemberDrawerOpen(false)}>取消</Button>
            <Button type="primary" loading={saving} onClick={submitMember}>
              保存
            </Button>
          </Space>
        }
      >
        <PersonFormBody
          form={memberForm}
          departmentOptions={departmentOptions}
          branchOptions={branchOptions}
          parentTreeSelectData={parentTreeSelectData}
          personSelectOptions={personSelectOptions}
          loadPersonOptions={loadPersonOptions}
        />
      </Drawer>

      <Modal
        title="分配系统账号"
        open={assignAccountOpen}
        onOk={submitAssignAccount}
        onCancel={() => setAssignAccountOpen(false)}
        confirmLoading={saving}
        okText="确认分配"
        cancelText="取消"
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          手机号作为登录用户名。若用户管理中已有该手机号账号，将自动绑定；否则系统自动生成初始密码（仅展示一次）。
        </Typography.Paragraph>
        <Form form={assignAccountForm} layout="vertical">
          {(personDetail?.mobile || '').trim() ? (
            <>
              <Form.Item name="useProfileMobile" label="手机号来源">
                <Radio.Group>
                  <Radio value={true}>使用现有手机号（{personDetail?.mobile?.trim() || '—'}）</Radio>
                  <Radio value={false}>使用其他手机号</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.useProfileMobile !== cur.useProfileMobile}>
                {({ getFieldValue }) =>
                  getFieldValue('useProfileMobile') === false ? (
                    <Form.Item
                      name="mobile"
                      label="登录手机号"
                      rules={[
                        { required: true, message: '请输入手机号' },
                        { pattern: /^1\d{10}$/, message: '请输入 11 位手机号' },
                      ]}
                    >
                      <Input placeholder="11 位手机号" maxLength={11} />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
            </>
          ) : (
            <Form.Item
              name="mobile"
              label="登录手机号"
              rules={[
                { required: true, message: '请输入手机号' },
                { pattern: /^1\d{10}$/, message: '请输入 11 位手机号' },
              ]}
            >
              <Input placeholder="11 位手机号" maxLength={11} />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title="新建下级分部"
        open={createOpen}
        onOk={submitCreate}
        onCancel={() => setCreateOpen(false)}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item name="parent_id" hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item name="unit_type" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如 华东分部" />
          </Form.Item>
          <Form.Item name="code" label="编码">
            <Input placeholder="可选" />
          </Form.Item>
          <Form.Item name="sort_order" label="显示顺序">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="编辑组织" open={editOpen} onOk={submitEdit} onCancel={() => setEditOpen(false)} confirmLoading={saving}>
        <Form form={editForm} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label="编码">
            <Input />
          </Form.Item>
          <Form.Item name="leader_name" label="负责人">
            <Input />
          </Form.Item>
          <Form.Item name="sort_order" label="显示顺序">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="状态正常" valuePropName="checked">
            <Switch checkedChildren="正常" unCheckedChildren="停用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
